import { useCallback, useMemo } from 'react';
import { ComponentProps } from '@app/participant-fields';
import { Criterion, Location } from 'queries';
import { setCriterionValues } from 'lib/criteria';
import { PlaceSelect } from '@app/form';
import { MultiValueProps } from 'react-select';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { Chip } from '@app/core';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

interface OptionType {
  value: string;
  placeId?: string;
  label: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    margin: theme.marginBase / 4,
    padding: 0,
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    width: 'fit-content',
    alignItems: 'stretch',

    '& p:first-letter': {
      textTransform: 'capitalize',
    },
  },
  text: {
    lineHeight: '150%',
    padding: [
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase,
    ],
  },
  removeIcon: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.marginBase / 2,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
}));

const MultiValue = (props: MultiValueProps<{ value: string }>) => {
  const tag = props.data.value;
  const classes = useStyles();
  return (
    <Chip
      color="green"
      className={classes.chip}
      {...props.innerProps}
      key={tag}
    >
      <p className={classes.text}>{tag}</p>
      <div className={classes.removeIcon} {...props.removeProps}>
        <i className={classnames('ri-close-line', classes.icon)} />
      </div>
    </Chip>
  );
};
const components = {
  MultiValue,
};

export const CriterionGeolocation = ({
  field,
  onChange,
  value: criterion,
  allowedFields,
  criteria,
  ...props
}: ComponentProps<Criterion>) => {
  const { t, i18n } = useTranslation();

  const locations = useMemo(() => {
    return (criterion?.allowList as Location[]) || [];
  }, [criterion?.allowList]);

  const current: OptionType[] = locations.map((value) => ({
    value: value.placeId || value.name,
    label: value.address || value.name,
  }));

  const descriptionText = 'criterion.description.' + field.name;
  const description = i18n.exists(descriptionText) && t(descriptionText);

  const onValueChange = useCallback(
    async (value: any[]) => {
      if (onChange) {
        onChange(
          setCriterionValues(
            criterion,
            value.map((val) => ({
              location: {
                lat: val?.geometry?.location?.lat(),
                lon: val?.geometry?.location?.lng(),
              },
              name: val.name,
              address: val.formatted_address,
              placeId: val.place_id,
            })),
          ),
        );
      }
    },
    [onChange, criterion],
  );

  return (
    <PlaceSelect
      {...props}
      value={current}
      isMulti
      label={field.label}
      placeholder={field.placeholder}
      secondaryLabel={description || ''}
      onValueChange={onValueChange}
    />
  );
};
